import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Send, Copy, Check } from "lucide-react";
import { sendWelcomeEmail } from "@/lib/send-welcome-email";

const ROLES = [
  "buyer", "seller", "broker", "consultant", "lender",
  "partner", "partner_lawyer", "partner_immigration_lawyer",
  "partner_notary", "partner_cpa", "partner_tax_advisor",
];

/**
 * Dev/QA only — direct call to the Firebase callable `sendWelcomeEmail`
 * for quick log verification (params, role, email, exact error).
 *
 * Wired in App.tsx at /dev/send-welcome-test.
 */
export default function DevSendWelcomeTest() {
  const [email, setEmail] = useState("test+welcome@example.com");
  const [firstName, setFirstName] = useState("Test");
  const [lastName, setLastName] = useState("User");
  const [role, setRole] = useState("buyer");
  const [language, setLanguage] = useState<"en" | "fr">("en");
  const [loading, setLoading] = useState(false);
  const [log, setLog] = useState<string>("");
  const [responseJson, setResponseJson] = useState<unknown>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const append = (line: string) => {
    const ts = new Date().toISOString();
    setLog((prev) => `${prev}[${ts}] ${line}\n`);
  };

  const buildParams = () => ({ email, firstName, lastName, role, language });

  const copy = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 1500);
    } catch (e) {
      console.error("Clipboard write failed", e);
    }
  };

  const onSend = async () => {
    setLoading(true);
    setResponseJson(null);
    const params = buildParams();
    append(`▶ Calling sendWelcomeEmail with ${JSON.stringify(params)}`);
    try {
      const res = await sendWelcomeEmail(params);
      setResponseJson({ ok: true, response: res });
      append(`✅ Success: ${JSON.stringify(res)}`);
    } catch (err: any) {
      const errPayload = {
        ok: false,
        code: err?.code ?? null,
        message: err?.message ?? "unknown",
        details: err?.details ?? null,
        name: err?.name ?? null,
      };
      setResponseJson(errPayload);
      append(`❌ Error: ${JSON.stringify(errPayload)}`);
      console.error("[DevSendWelcomeTest] Full error:", err);
    } finally {
      setLoading(false);
    }
  };

  const responseStr = responseJson ? JSON.stringify(responseJson, null, 2) : "";
  const paramsStr = JSON.stringify(buildParams(), null, 2);

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-2xl mx-auto bg-card rounded-xl shadow-lg p-6 border">
        <h1 className="text-2xl font-bold mb-2">🧪 Dev — sendWelcomeEmail</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Direct call to the Firebase callable. Open the console for detailed logs
          (client-side: <code>[sendWelcomeEmail/client]</code>, backend: Firebase Functions logs).
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="role">Role</Label>
            <select
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-input bg-background"
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="firstName">First name</Label>
            <Input id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="lastName">Last name</Label>
            <Input id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="lang">Language</Label>
            <select
              id="lang"
              value={language}
              onChange={(e) => setLanguage(e.target.value as "en" | "fr")}
              className="w-full h-10 px-3 rounded-md border border-input bg-background"
            >
              <option value="en">en</option>
              <option value="fr">fr</option>
            </select>
          </div>
        </div>

        <div className="flex gap-2 mb-4">
          <Button onClick={onSend} disabled={loading} className="flex-1">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Trigger sendWelcomeEmail
          </Button>
          <Button
            variant="outline"
            onClick={() => copy("params", paramsStr)}
            title="Copy parameters JSON"
          >
            {copied === "params" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            <span className="ml-2">Copy params</span>
          </Button>
        </div>

        <div className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <Label>Response (full JSON)</Label>
            <Button
              size="sm"
              variant="ghost"
              disabled={!responseStr}
              onClick={() => copy("response", responseStr)}
            >
              {copied === "response" ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
              Copy response
            </Button>
          </div>
          <pre className="p-3 bg-muted rounded-md text-xs whitespace-pre-wrap break-words max-h-72 overflow-auto border">
{responseStr || "(no response yet)"}
          </pre>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <Label>Log</Label>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                disabled={!log}
                onClick={() => copy("log", log)}
              >
                {copied === "log" ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                Copy log
              </Button>
              <Button size="sm" variant="ghost" disabled={!log} onClick={() => setLog("")}>
                Clear
              </Button>
            </div>
          </div>
          <pre className="p-3 bg-muted rounded-md text-xs whitespace-pre-wrap break-words max-h-96 overflow-auto border">
{log || "(no call yet)"}
          </pre>
        </div>
      </div>
    </div>
  );
}
